import { CheckCircle2, ExternalLink, Pencil, Trash2, X } from 'lucide-react';
import { MoneyAmount } from '@/components/design-system';
import { StatutPaiementBadge, isPaiementEnRetard } from '@/components/finances/StatutPaiementBadge';
import type { PaiementFormateurRow } from '@/hooks/queries/usePaiementsFormateurs';
import { cn } from '@/lib/utils';

interface Props {
  paiement: PaiementFormateurRow | null;
  onClose: () => void;
  onMarquerPaye: (p: PaiementFormateurRow) => void;
  onEdit: (p: PaiementFormateurRow) => void;
  onDelete: (p: PaiementFormateurRow) => void;
}

export function PaiementFormateurDrawer({
  paiement,
  onClose,
  onMarquerPaye,
  onEdit,
  onDelete,
}: Props): JSX.Element {
  if (!paiement) return <></>;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-2xl sm:max-w-lg"
        role="dialog"
        aria-label="Détail paiement formateur"
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 p-4">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Paiement formateur
            </div>
            <h2 className="mt-1 truncate text-lg font-bold text-slate-900">
              {paiement.formateur_nom}
            </h2>
            <div className="mt-1 flex items-center gap-2">
              <StatutPaiementBadge
                statut={paiement.statut}
                enRetard={isPaiementEnRetard(paiement.statut, paiement.date_deroulement)}
              />
              <span className="text-xs text-slate-500">
                {paiement.annee}
                {paiement.periode ? ` · ${paiement.periode}` : ''}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => onEdit(paiement)}
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Modifier"
              title="Modifier"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(paiement)}
              className="rounded-md p-1.5 text-rose-500 hover:bg-rose-50 hover:text-rose-700"
              aria-label="Supprimer"
              title="Supprimer"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>
        <div className="flex-1 space-y-5 overflow-y-auto p-4">
          <div className="rounded-xl bg-gradient-to-br from-violet-50 to-purple-100 p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-violet-700">
              Montant
            </div>
            <div className="mt-1">
              <MoneyAmount amount={paiement.montant} variant="kpi" />
            </div>
          </div>

          <Section title="Formation">
            <Field label="Titre" value={paiement.formation_titre ?? '—'} />
            <Field label="Date déroulement" value={paiement.date_deroulement ?? '—'} />
          </Section>

          <Section title="Coordonnées formateur">
            <Field label="RIB / Coordonnées" value={paiement.formateur_coordonnees ?? '—'} mono />
          </Section>

          {paiement.statut === 'paye' && (
            <Section title="Règlement">
              <Field label="Date" value={paiement.date_reglement ?? '—'} />
              <Field label="Mode" value={paiement.mode_paiement ?? '—'} />
              <Field label="Référence opération" value={paiement.reference_operation ?? '—'} mono />
              {paiement.preuve_paiement_url && (
                <a
                  href={paiement.preuve_paiement_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-violet-600 hover:underline"
                >
                  <ExternalLink className="h-3 w-3" /> Voir la preuve
                </a>
              )}
              {paiement.operation_id && (
                <div className="mt-1 text-[11px] text-slate-500">
                  Lié à l&apos;opération{' '}
                  <span className="font-mono">{paiement.operation_id.slice(0, 8)}…</span>
                </div>
              )}
            </Section>
          )}

          {paiement.remarques && (
            <Section title="Remarques">
              <p className="text-sm text-slate-700">{paiement.remarques}</p>
            </Section>
          )}
        </div>
        <footer className="border-t border-slate-200 p-4">
          {paiement.statut === 'a_payer' ? (
            <button
              type="button"
              onClick={() => onMarquerPaye(paiement)}
              className={cn(
                'inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-700',
              )}
            >
              <CheckCircle2 className="h-4 w-4" /> Marquer payé · créer opération
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Fermer
            </button>
          )}
        </footer>
      </aside>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section>
      <h3 className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
        {title}
      </h3>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={cn('text-right font-medium text-slate-800', mono && 'font-mono text-xs')}>
        {value}
      </span>
    </div>
  );
}
